import React, { useMemo, useState } from "react";
import { FetchBoundary } from "../../../common/FetchBoundary";
import { useFetch } from "../../../Hooks/useFetch";
import { Search } from "@material-ui/icons";
import { InputAdornment, Select, TextField } from "@mui/material";
import { humanizeText } from "../../../utils/StringFormatters";
import { MonthlyTransactionRow } from "./MonthlyTransactionRow";
import { sum, sumFloat } from "../../../utils/NumberFormatter";
import { Toggle } from "../../../common/Inputs/Toggle";
import { CheckMenuItem } from "../../../common/Inputs/CheckMenuItem";

export const AllTransactionsTable = ({ account }) => {
  const [searchInput, setSearchInput] = useState("");
  const [unvalidatedPurchases, setUnvalidatedPurchases] = useState(false);
  const [productNameInput, setProductNameInput] = useState("all");

  const monthlyTransactionsFetcher = useFetch(
    `/api/covered_entities/${account.id_340b}/accounts/${account.id}/monthly_transactions?unvalidated=${unvalidatedPurchases}`
  );

  const monthlyTransactions = monthlyTransactionsFetcher.data?.monthly_transactions ?? [];

  const filteredMonthlyTransactions = useMemo(() => {
    const filterTransaction = (p) =>
      (searchInput.length === 0 || p.ndc_11.includes(searchInput.replaceAll("-", ""))) &&
      (productNameInput === "all" || p.product_name === productNameInput);

    const includeEmptyRows =
      productNameInput === "all" && searchInput.length === 0 && !unvalidatedPurchases;

    return monthlyTransactions
      .map((pm) => ({ ...pm, transactions: pm.transactions.filter(filterTransaction) }))
      .filter((pm) => includeEmptyRows || pm.transactions.length > 0)
      .map((pm) => {
        return {
          ...pm,
          transactions: pm.transactions.sort((a, b) => a.ndc_11 - b.ndc_11),
          claim_units: sumFloat(pm.transactions, "claim_units"),
          purchase_count: sum(pm.transactions, "purchase_count"),
          purchase_units: sumFloat(pm.transactions, "purchase_units"),
        };
      });
  }, [searchInput, productNameInput, unvalidatedPurchases, monthlyTransactions]);

  const productNames = [
    ...new Set(
      monthlyTransactions
        .flatMap((mt) => mt.transactions)
        .map((t) => t.product_name)
        .sort()
    ),
  ];

  const handleUnvalidatedPurchaseChange = (value) => {
    setUnvalidatedPurchases(value);
    setProductNameInput("all");
  };

  return (
    <div>
      <div>
        <div className="t--500 pb-2">All Transactions (Last Twelve Months)</div>
        <div className="t--sm t--secondary">
          View all purchases and claims from the last twelve months
        </div>
      </div>

      <div className="flex align-items-center justify-content-between">
        <div className="flex align-items-center gap--8">
          <TextField
            label=""
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search for an NDC-11"
            className="my-4"
            style={{ width: 225 }}
            sx={{
              "& .MuiInputBase-root": {
                height: 36,
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
          <div>
            <Select
              value={productNameInput}
              label=""
              onChange={(e) => setProductNameInput(e.target.value)}
              sx={{ height: 36 }}
            >
              <CheckMenuItem value="all">All Products</CheckMenuItem>
              {productNames.map((productName) => (
                <CheckMenuItem key={productName} value={productName}>
                  {humanizeText(productName)}
                </CheckMenuItem>
              ))}
            </Select>
          </div>
        </div>

        <Toggle
          setValue={handleUnvalidatedPurchaseChange}
          enabledValue={true}
          disabledValue={false}
          label={"Unvalidated Purchases Only"}
        />
      </div>

      <FetchBoundary {...monthlyTransactionsFetcher}>
        {filteredMonthlyTransactions.length > 0 ? (
          <div className="t--left t--sm container-fluid row-border-rounded">
            <div className="row align-items-center py-3 row-header t--500">
              <div className="col col-1"></div>
              <div className="col">Month</div>
              <div className="col">Purchases</div>
              <div className="col">Purchase Units</div>
              <div className="col">Claim Units</div>
            </div>
            {filteredMonthlyTransactions.map((monthlyTransaction) => (
              <MonthlyTransactionRow
                key={monthlyTransaction.month}
                monthlyTransaction={monthlyTransaction}
              />
            ))}
          </div>
        ) : (
          <div className="bg--light-navy t--500 flex align-items-center justify-content-center border--rounded t--sm t--secondary w--full h--96">
            No transactions
          </div>
        )}
      </FetchBoundary>
    </div>
  );
};
